<script setup lang="ts">
import {
  CURRENT_RUNNING_WS_SERVICE,
  INIT_NEXT_ACTION
} from '@/components/organisation/enum/advanceCompiler.enums'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useIdeStore } from '@/stores/ide.store'
import htmlExecuteService from '@/services/ide/languages/html/execute.service'
import BrowserTab from '@/components/ide/ide/languages/html/BrowserTab.vue'
import { useAdvanceCompilerStore } from '@/stores/advanceCompiler.store'

import animatingLogo from '@/assets/images/ide/animating_logo.gif'

const ideStore = useIdeStore()
const advanceCompilerStore = useAdvanceCompilerStore()

const formActionMethod = ref<string>('post')
const addressBarPath = ref<string>('')
const iframeSource = ref<string>('')
const outputFrameRef = ref<HTMLIFrameElement | null>(null)

const serverMessageRef = ref<HTMLDivElement | null>(null)
const isAdvanced = computed(() => {
  return ideStore.isAdvanced
})
const projectKey = computed(() => {
  return ideStore.projectKey
})
const projectHome = computed(() => {
  return ideStore.project?.home
})
const logMessages = computed(() => {
  return advanceCompilerStore.logMessages
})

const userStartedService = computed(() => {
  return ideStore.userStartedService
})

const serviceRunning = computed(() => {
  return ideStore.advanceServiceRunning
})

const socketInProgress = computed(() => {
  return (
    ideStore.isAdvancedCompiler &&
    (advanceCompilerStore.socketInProgress ||
      ((ideStore.userStartedServiceUsingExecute || ideStore.userStoppedServiceUsingStopExecution) &&
        userStartedService.value !== serviceRunning.value))
  )
})

const socketLogsInProgress = computed(() => {
  return advanceCompilerStore.socketLogsInProgress && ideStore.isAdvancedCompiler
})
const inprogressTest = computed(() => {
  if (advanceCompilerStore.initNextAction == INIT_NEXT_ACTION.WAIT) {
    return 'Something went wrong please wait while we restart the machine...'
  }
  if (advanceCompilerStore.currentWsServiceAdvCompiler === CURRENT_RUNNING_WS_SERVICE.STOP) {
    return 'Stopping Service. Please Wait...'
  } else return 'Starting Service. Please Wait...'
})

const isHtmlFile = computed(() => {
  return (
    !ideStore.currentIFrameSourceBrowser ||
    ideStore.currentIFrameSourceBrowser == '/index.html' ||
    ideStore.currentIFrameSourceBrowser == ideStore?.project?.home
  )
})

const nonHtmlFile = computed(() => {
  return (
    !ideStore.currentIFrameSourceBrowser ||
    ideStore.currentIFrameSourceBrowser != '/index.html' ||
    ideStore.currentIFrameSourceBrowser != ideStore?.project?.home
  )
})

/**
 * Sets the form  action
 * @returns the href for the iframe
 */
const formAction = () => {
  if (isAdvanced.value) {
    iframeSource.value = `/api/doodle/html-multi-web/${projectKey.value}${addressBarPath.value}`
    formActionMethod.value = 'get'
    return iframeSource.value
  } else {
    formActionMethod.value = 'post'

    return '/api/doodle/download/web.html'
  }
}
/**
 * Wait for the code editor to be available
 * @param count - the count
 */
const postTryExecute = async (count: number = 0) => {
  if (count < 200 && useIdeStore().isIdeView && useIdeStore().codeEditor === null) {
    await new Promise((resolve) => setTimeout(resolve, 10))
    await postTryExecute(count + 1)
  } else await new Promise((resolve) => setTimeout(resolve, 500))
}
/**
 * Tries to execute the code
 */
const tryExecute = async () => {
  await postTryExecute()

  if (ideStore.isHtml && isHtmlFile.value) {
    htmlExecuteService.tryExecute(false)
  } else if (ideStore.isHtml && nonHtmlFile.value) {
    if (outputFrameRef.value) {
      outputFrameRef.value.src = ideStore.currentIFrameSourceBrowser || ''
    }
  } else if (ideStore.isAdvancedCompiler) {
    if (ideStore.currentIFrameSourceBrowser) {
      if (outputFrameRef.value) {
        outputFrameRef.value.src = ideStore.currentIFrameSourceBrowser
      }
    } else if (
      advanceCompilerStore.startPodsCompleted ||
      advanceCompilerStore.isPodInitializationCompleted
    ) {
      if (outputFrameRef.value) {
        outputFrameRef.value.src = advanceCompilerStore.subDomainUrl || ''
      }
    } else {
      if (outputFrameRef.value) {
        outputFrameRef.value.src = advanceCompilerStore.subDomainUrl || ''
      }
    }
  }

  if (projectHome.value && projectHome.value != undefined) {
    addressBarPath.value = projectHome.value
  }
}

onMounted(async () => {
  await tryExecute()
})

watch(projectHome, (newValue) => {
  addressBarPath.value = newValue
})
watch(isAdvanced, async () => {
  await tryExecute()
})

watch(
  () => [ideStore.isHtml, ideStore.isAdvancedCompiler],
  async () => {
    ideStore.setCurrentIframeSourceBrowser('')
    await tryExecute()
  }
)

watch(
  () => advanceCompilerStore.startPodsCompleted,
  async (value) => {
    if (value) {
      await nextTick()
      if (outputFrameRef.value) {
        outputFrameRef.value.src = advanceCompilerStore.subDomainUrl || ''
      }
    }
  }
)

watch(
  () => ideStore.advanceServiceRunning,
  async (value) => {
    if (value === false) {
      await nextTick()
      if (outputFrameRef.value) {
        outputFrameRef.value.src = advanceCompilerStore.subDomainUrl || ''
      }
    }
  }
)

watch(
  () => advanceCompilerStore.isPodInitializationCompleted,
  async () => {
    await nextTick()

    if (outputFrameRef.value) {
      outputFrameRef.value.src = advanceCompilerStore.subDomainUrl || ''
    }
  }
)

watch(
  () => logMessages.value,
  async () => {
    await nextTick()
    const container = serverMessageRef.value
    if (container) {
      container.scrollTop = container.scrollHeight
    }
  }
)
</script>
<template>
  <div
    class="flex h-full w-full flex-col gap-2 print:break-inside-avoid print:border print:p-1"
    :class="[{ 'px-1 pb-1': isAdvanced }, { 'px-1 pb-1 ': !isAdvanced }]"
  >
    <BrowserTab :iframeRef="outputFrameRef" />

    <div
      v-if="
        !socketInProgress &&
        !advanceCompilerStore.startLoggingMessages &&
        advanceCompilerStore.serverErrorsMessages == null &&
        !advanceCompilerStore.loadingPods
      "
      class="h-full w-full rounded-sm bg-white"
      :class="isAdvanced ? 'h-full' : 'h-96'"
    >
      <iframe
        ref="outputFrameRef"
        name="output"
        frameborder="0"
        height="100%"
        width="100%"
        sandbox=" allow-forms allow-popups allow-scripts allow-same-origin allow-modals"
      ></iframe>
    </div>

    <div
      v-else
      class="h-full overflow-y-auto border border-gray-300 bg-gray-100 p-2.5 text-xs"
      ref="serverMessageRef"
    >
      <div
        class="mb-1.5 font-mono text-black"
        v-if="advanceCompilerStore.serverErrorsMessages !== null"
      >
        {{ advanceCompilerStore.serverErrorsMessages }}
      </div>
      <div
        class="mb-1.5 flex h-full flex-col items-center justify-center text-center font-mono text-black"
        v-else-if="socketInProgress && !socketLogsInProgress"
      >
        <img :src="animatingLogo" class="mx-auto h-24 w-24" />
        {{ inprogressTest }}
      </div>
      <div v-else class="mb-1.5 font-mono text-black">
        <img :src="animatingLogo" class="h-24 w-24" />
        <div v-for="(message, index) in logMessages" :key="index">
          {{ message }}
        </div>
      </div>
    </div>
  </div>
  <div class="hidden">
    <form
      id="outputForm"
      name="html-download-form"
      :method="formActionMethod"
      :action="formAction()"
    >
      <textarea id="outputTextArea" name="code"></textarea>
    </form>
  </div>
</template>
